import * as AttendanceApi from "../../../api/Attendance/attendance";
import moment from "moment";
import { ClockingType, DaySummaryView, WorkplaceConfig } from "../../../models/attendance";
import { Dispatch } from "redux";
import { GetNotificationsFirstPagePerUser } from "../../../api/General/Notification";
import { getUserInfo as getAccountUserInfo } from "../../../api/General/account";
import { UserView } from "../../../models/general";
import { anyUserWithVariableScheduleRule as apiAnyUserWithVariableScheduleRule } from "../../../api/Attendance/shift";
import { formatLocalDateParam, parseApiUtc } from "../../../utils/apiDate";

export const INIT = "INIT";
export const SET_LOADING = "SET_LOADING";
export const SET_FIRST_LOAD = "SET_FIRST_LOAD";
export const SET_PREVURL = "SET_PREVURL";
export const LOGOUT = "LOGOUT";
export const GET_STATUS = "GET_STATUS";
export const UPDATE_CLOCKING_TIMERS = "UPDATE_CLOCKING_TIMERS";
export const ANY_USER_WITH_VARIABLE_SCHEDULE_RULE = "ANY_USER_WITH_VARIABLE_SCHEDULE_RULE";
export const SET_USER_FACE_IMAGE = "SET_USER_FACE_IMAGE";
export const SET_IMAGE_URL = "SET_IMAGE_URL";
export const SET_FACE_RECOGNITION_MODELS_LOADED = "SET_FACE_RECOGNITION_MODELS_LOADED";
export const SET_LABELED_FACE_DESCRIPTORS = "SET_LABELED_FACE_DESCRIPTORS";
export const SET_LOCALE = "SET_LOCALE";
export const SET_NOTIFICATIONS = "SET_NOTIFICATIONS";
export const SET_SELECTED_USER = "SET_SELECTED_USER";
export const SET_SELECTED_USER_MANAGERS = "SET_SELECTED_USER_MANAGERS";
export const SET_USER_AUTH_INFO = "SET_USER_AUTH_INFO";
export const SET_CURRENT_STATUS_TEXT = "SET_CURRENT_STATUS_TEXT";
export const SET_COMPANY_PRIMARY_COLOR = "SET_COMPANY_PRIMARY_COLOR";
export const UPDATE_COMPANY_RULES = "UPDATE_COMPANY_RULES";
export const UPDATE_USER_RULES = "UPDATE_USER_RULES";
export const UPDATE_USER_ROLE_INFO = "UPDATE_USER_ROLE_INFO";
export const SET_BOOT_ERROR = "SET_BOOT_ERROR";


interface IUserInfo {
  userId: any,
  isLoggedIn: any,
  company: any,
  username: any,
  title: any,
  firstName: any,
  lastName: any,
  fullName: any,
  linkedInUrl: any,
  birthDate: any,
  currentStatus: any,
  currentCustomStatusText: any,
  role: any,
  isManager: any,
  imageUrl: any,
  attendanceImageConfig: any,
  workplaceConfig: WorkplaceConfig,
  userFaceRecognitionConfig: any,
  locale: any,
  rtl: any,
  firstLogin: any,
  userFaceImage: any,
  labeledFaceDescriptors: any,
  companyName: any,
  companyLogo: any,
  companyPrimaryColor: any,
  companyLocales: string,
  hireDate: any,
  companyCurrency: any,
  companyCurrencyId: any,
  userCurrencyRule: any,
  UserCurrencyId: any,
  UserCurrency: any,
  incrementWorkingTimer: any,
  incrementBreakTimer: any,
  currentShift: any,
  isTimeRunning: any,
  isBreakTimeRunning: any,
  breaksTime: any;
  totalBreaksTime: any;
  today: DaySummaryView;
  totalWorkingTime: any;
  workingTime: any;
  currentWorkingDuration: any;
  allowedActions: ClockingType[];
  anyUserWithVariableScheduleRule: boolean;
  dayHasMultipleShifts: boolean;
  selectedUser: UserView | undefined;
  selectedUserManagers: UserView[];
}

export const init = (input: IUserInfo) => {
  return {
    type: INIT,
    payload: input
  }
}

export const setUserAuthInfo = (input: any) => {
  return {
    type: SET_USER_AUTH_INFO,
    payload: input
  }
}

// Cleanup-only Redux logout. Pause persistence so redux-persist doesn't re-write
// state after LOGOUT, dispatch LOGOUT (root reducer resets every slice preserving
// locale/rtl), purge persist:root explicitly, then resume persistence so the
// next login session writes normally.
//
// This is the post-redirect cleanup called from LogoutCallback.tsx after the
// OIDC end-session redirect completes. It does NOT trigger the OIDC flow — that
// is AuthService.logout() (voluntary) or forceLogout() (involuntary).
export const logout = () => {
  return async (dispatch: Dispatch) => {
    try {
      const { getPersistor } = await import("../store/store");
      const persistor = getPersistor();
      persistor?.pause();
      dispatch({ type: LOGOUT });
      await persistor?.purge();
      persistor?.persist();
    } catch (err) {
      console.error("[auth] logout cleanup failed:", err);
      dispatch({ type: LOGOUT });
    }
  };
};

export const setPrevUrl = (input: string) => {
  return {
    type: SET_PREVURL,
    payload: input
  }
}
export const setCompanyPrimaryColor = (color: string) => ({
  type: SET_COMPANY_PRIMARY_COLOR,
  payload: color,
});
export const setLoading = (input: boolean) => {
  return {
    type: SET_LOADING,
    payload: input
  }
}

// Boot could not produce a user profile (GetUserInfo rejected/timed out, or
// INIT was handed an undefined payload). PrivateRoute turns this into a visible
// "Couldn't load your profile — Retry" panel instead of the old silent shell.
export const setBootError = (input: boolean) => {
  return {
    type: SET_BOOT_ERROR,
    payload: input
  }
}

export const setFirstLoad = (input: boolean) => {
  return {
    type: SET_FIRST_LOAD,
    payload: input
  }
}

export const setUserFaceImage = (input: any) => {
  return {
    type: SET_USER_FACE_IMAGE,
    payload: input
  }
}

export const setImageUrl = (input: string) => {
  return {
    type: SET_IMAGE_URL,
    payload: input
  }
}

export const getStatus = (userId: string) => {
  return async (dispatch: Dispatch) => {
    try {
      const status = await AttendanceApi.getStatus();
      if (status.status === 200) {
        const todayParam = formatLocalDateParam(new Date());
        const today = await AttendanceApi.getClockings(
          userId,
          todayParam,
          todayParam
        );

        // Refresh user availability status too (currentStatus / custom status text)
        // This is needed because the attendance/status endpoint doesn't include those fields.
        let currentStatus: any = undefined;
        let currentCustomStatusText: any = undefined;
        try {
          const userInfo = await getAccountUserInfo();
          if ((userInfo as any)?.status === 200) {
            currentStatus = (userInfo as any)?.payload?.currentStatus;
            // Backend historically used a misspelled field name
            currentCustomStatusText =
              (userInfo as any)?.payload?.currentCustmStatusText ??
              (userInfo as any)?.payload?.currentCustomStatusText;
          }
        } catch {
          // Non-fatal: clocking status should still update
        }

        dispatch({
          type: "GET_STATUS",
          payload: {
            allowedActions: status.payload.allowedActions,
            currentShift: status.payload.currentShift,
            // 🔴 FL-88 — the server-computed gauge. Always present; `?? null` only guards a
            // mid-deploy bundle talking to a server that predates the field.
            gauge: status.payload.gauge ?? null,
            today: today.payload.results[0],
            incrementWorkingTimer: 0,
            incrementBreakTimer: 0,
            dayHasMultipleShifts: today.payload.results[0].shifts.length > 1,
            currentStatus,
            currentCustomStatusText,
          },
        });
      }
    } catch (error) {
      console.error("Failed to get status:", error);
    }
  };
};

export const updateAnyUserWithVariableScheduleRule = (input: boolean) => {
  return {
    type: 'ANY_USER_WITH_VARIABLE_SCHEDULE_RULE',
    payload: input
  }
}

// Thunk: fetch and update whether any user has variable schedule rule
export const fetchAnyUserWithVariableScheduleRule = () => {
  return async (dispatch: Dispatch) => {
    try {
      const res = await apiAnyUserWithVariableScheduleRule();
      // API returns Result<boolean>
      if (res && typeof (res as any).status === 'number') {
        const ok = (res as any).status === 200;
        const value = ok ? (res as any).payload : false;
        dispatch(updateAnyUserWithVariableScheduleRule(!!value));
      } else {
        // fallback if API changed to return plain boolean
        dispatch(updateAnyUserWithVariableScheduleRule(!!res));
      }
    } catch (e) {
      // On failure, do not crash app; just set to false conservatively
      dispatch(updateAnyUserWithVariableScheduleRule(false));
    }
  };
};

export const setFaceRecognitionModelsLoaded = (input: boolean) => {
  return {
    type: 'SET_FACE_RECOGNITION_MODELS_LOADED',
    payload: input
  }
}

export const setLabeledFaceDescriptors = (input: any) => {
  return {
    type: 'SET_LABELED_FACE_DESCRIPTORS',
    payload: input
  }
}

export const updateClockingTimers = () => {
  return (dispatch: Dispatch, getState: () => any) => {
  const state = getState();
  const { today, currentShift, dayHasMultipleShifts, gauge } = state.user || {};

    const now = moment(new Date());

    const isTimeRunning = currentShift && !currentShift.hasEnded;
    
    let isBreakTimeRunning = false;
    let activeBreak = null;
    let breakInTimeStart = null;
    const breaksTime = currentShift ? moment.duration(currentShift.breaksDuration) : moment.duration(0);
    if (currentShift && currentShift.breaks) {
      isBreakTimeRunning = currentShift.breaks.filter((x: any) => !x.hasEnded).length > 0;

      activeBreak = currentShift.breaks.filter((x: any) => !x.hasEnded)[0];
      if (activeBreak) {
        breakInTimeStart = moment(activeBreak.breakInTime);
        breaksTime.add(moment.duration(now.diff(breakInTimeStart)));
      }
    }

    const totalBreaksTime = today ? moment.duration(today.breaksTime) : moment.duration(0);
    if (today && today.shifts && today.shifts.length > 0) 
      totalBreaksTime.add(breaksTime);
    if (currentShift) 
      totalBreaksTime.subtract(currentShift.breaksDuration);

    let start = null;
    if (currentShift)
      start = moment(currentShift.clockInTime);
    
    let totalWorkingTime = today ? moment.duration(today.workingTime) : moment.duration(0);

    // 🔴 FL-88 — `workingTime` is THE GAUGE NUMBER and it is now SERVER-COMPUTED.
    //
    // What was here before recomputed it on the client as `now − currentShift.clockInTime − breaks`.
    // That was web's half of the drift this ticket exists to close, three ways at once:
    //   (a) it measured ONE session, so the number restarted at 00:00:00 on a second clock-in of the
    //       same scheduled shift — the owner's actual complaint;
    //   (b) it re-derived from raw timestamps, so the client and the server could disagree about
    //       which punches count;
    //   (c) it silently disagreed with the ring, which was drawn from a different definition again.
    //
    // The rule now: SEED from `gauge.workedDuration` on every `/Status`, and tick +1 s locally ONLY
    // while the server says a session is open and no break is running. An open break freezes the
    // number server-side, so the client must freeze too or it would drift away between polls.
    //
    // ⚠️ `totalWorkingTime` below is DELIBERATELY LEFT ALONE. It is the whole-day total — a
    // different, correct number — and during an overnight shift it WILL differ from the gauge,
    // because the day total splits at local midnight and an occurrence does not. Both are right.
    let workingTime = moment.duration(0);

    if (gauge && gauge.scope && gauge.scope !== 'None') {
      workingTime = moment.duration(gauge.workedDuration);
      if (gauge.hasOpenSession && !gauge.isOnBreak) {
        // Elapsed since the server's own `asOfUtc`, so a slow poll cycle catches up in one step
        // instead of accumulating +1 s ticks that can never be reconciled.
        const asOf = parseApiUtc(gauge.asOfUtc);
        const elapsedMs = asOf ? Math.max(0, Date.now() - asOf.getTime()) : 0;
        workingTime = workingTime.add(elapsedMs, 'milliseconds');
      }
    } else if (currentShift && !currentShift.hasEnded) {
      // No gauge on the wire at all (a bundle newer than its server, mid-deploy). Fall back to the
      // per-session figure the server already sends rather than re-deriving anything locally.
      workingTime = moment.duration(currentShift.workingDuration);
    }

    // The DAY total's own live-elapsed input, kept byte-identical to what it was before FL-88 moved
    // the gauge number to the server. It is intentionally NOT `workingTime` any more: on an overnight
    // occurrence the gauge spans midnight and the day total does not, so feeding one into the other
    // would quietly redefine the back card.
    let sessionElapsed = moment.duration(0);
    if (currentShift && !currentShift.hasEnded) {
      sessionElapsed = isBreakTimeRunning
        ? moment.duration(currentShift.workingDuration)
        : moment.duration(now.diff(start)).subtract(breaksTime);
    }

    if (today && currentShift && !currentShift.hasEnded) {
      if (currentShift.breaks) {
        if (!activeBreak) {
          if (!dayHasMultipleShifts) {
            totalWorkingTime = sessionElapsed;
          } else {
            totalWorkingTime.subtract(currentShift.workingDuration);
            totalWorkingTime.add(now.diff(start)).subtract(breaksTime);
          }
        }
      }
    }


    dispatch({
      type: UPDATE_CLOCKING_TIMERS,
      payload: {
        isTimeRunning,
        isBreakTimeRunning,
        breaksTime,
        totalBreaksTime,
        workingTime,
        totalWorkingTime
      }
    });
  };
}

export const setLocale = (locale: string) => ({
  type: SET_LOCALE,
  payload: { locale },
});

export const getNotifications = () => {
  return async (dispatch: Dispatch, getState: () => any) => {
    const state = getState();
    const { userId } = state;

    const { payload } = await GetNotificationsFirstPagePerUser(userId);
    dispatch({
      type: SET_NOTIFICATIONS,
      payload: {
        fullListNotifications: payload.notificationsList,
        notifications: payload.notificationsList,
        notificationsTotalCount: payload.notificationsTotalCount,
        notificationsTotalUnreadCount: payload.notificationsTotalUnreadCount
      }
    });
  }
}

export const setSelectedUser = (input: UserView | undefined) => {
  return {
    type: 'SET_SELECTED_USER',
    payload: input
  }
}

export const setSelectedUserManagers = (input: UserView[]) => {
  return {
    type: 'SET_SELECTED_USER_MANAGERS',
    payload: input
  }
}

export const setCurrentStatusText = (input: string) => {
  return {
    type: SET_CURRENT_STATUS_TEXT,
    payload: input
  }
}

export const updateCompanyRules = (rules: any) => {
  return {
    type: UPDATE_COMPANY_RULES,
    payload: rules
  }
}

export const updateUserRules = (rules: any) => {
  return {
    type: UPDATE_USER_RULES,
    payload: rules
  }
}

// Role/tag reconciliation for a rehydrated session. INIT is the only other writer of
// `role` / `isManager` / `isAssetManager`, and it is gated on `firstLoad` (PrivateRoute),
// which redux-persist stores as false — so without this a grant/revoke of the
// AssetManagement tag (or a role change) only took effect after a full logout/login.
export const updateUserRoleInfo = (info: { role?: string; isManager?: boolean; isAssetManager?: boolean }) => {
  return {
    type: UPDATE_USER_ROLE_INFO,
    payload: info
  }
}

// Background "stale-while-revalidate" reconcile for the persisted user snapshot.
//
// The whole Redux tree is cached in localStorage (redux-persist, key 'root'),
// and the authoritative refresh in PrivateRoute is gated on `firstLoad`, which
// INIT flips to false and then persists. So a returning (rehydrated) session
// never re-fetches getUserInfo and keeps running on a possibly-stale company /
// user-rules snapshot — e.g. the attendance-image / face-recognition config that
// decides whether the clocking dialog opens the camera (and whether FR runs).
// SignalR (CompanyRulesUpdated) only covers changes made WHILE the app is open;
// a change applied while it was closed is never reconciled until a full re-login
// (which purges persist:root) or a manual re-save of the rule.
//
// This thunk re-fetches getUserInfo once on app load and updates Redux ONLY for
// the config fields that actually changed — keeping the instant render from
// cache, with no churn when nothing changed.
export const revalidateUserConfig = () => {
  return async (dispatch: Dispatch, getState: () => any) => {
    try {
      const res: any = await getAccountUserInfo();
      if (res?.status !== 200 || !res.payload) return;
      const payload = res.payload;
      const current = getState()?.user ?? {};

      // Company-level attendance / face-recognition / timesheet config.
      const fresh = payload.company ?? {};
      const prev = current.company ?? {};
      const companyChanged =
        fresh.attendanceImageConfig !== prev.attendanceImageConfig ||
        fresh.faceRecognitionConfig !== prev.faceRecognitionConfig ||
        fresh.faceRecognitionEnabled !== prev.faceRecognitionEnabled ||
        fresh.hideClockingImagesFromTimesheet !== prev.hideClockingImagesFromTimesheet ||
        fresh.shiftSwapEnabled !== prev.shiftSwapEnabled ||
        fresh.billingEnabled !== prev.billingEnabled;
      if (companyChanged) {
        dispatch(updateCompanyRules({
          attendanceImageConfig: fresh.attendanceImageConfig,
          faceRecognitionConfig: fresh.faceRecognitionConfig,
          faceRecognitionEnabled: fresh.faceRecognitionEnabled,
          hideClockingImagesFromTimesheet: fresh.hideClockingImagesFromTimesheet,
          // Without this, a company that toggled shift-swap off/on while the app was
          // closed keeps a stale value in persist:root, so the /shift-swaps route guard
          // (ShiftSwapEnabledRoute) reads the old flag and fails to redirect (NAV-3).
          shiftSwapEnabled: fresh.shiftSwapEnabled,
          // CO-239: same NAV-3 staleness problem, one level up — this is an ENVIRONMENT switch, so
          // it flips on a DEPLOY rather than on a company edit, and every persisted snapshot written
          // before the field existed carries `undefined`. The nav item and the /settings/billing route
          // both fail closed on that, so without this line an environment where billing IS on would
          // keep the entry hidden for a returning session until a full re-login.
          billingEnabled: fresh.billingEnabled,
        }));
      }

      // Role + additive-tag reconciliation. Drives RoleProtectedRoute, useFilteredNavigation
      // and useCanManageAssets, none of which would otherwise notice a change made while the
      // app was closed (or in another tab) until a full re-login.
      const freshIsAssetManager = payload.isAssetManager === true;
      const roleInfoChanged =
        (payload.role != null && payload.role !== current.role) ||
        (payload.isManager != null && payload.isManager !== current.isManager) ||
        freshIsAssetManager !== (current.isAssetManager === true);
      if (roleInfoChanged) {
        dispatch(updateUserRoleInfo({
          role: payload.role ?? current.role,
          isManager: payload.isManager ?? current.isManager,
          isAssetManager: freshIsAssetManager,
        }));
      }

      // Per-user rule overrides for the same attendance config.
      const freshRules = payload.userRules ?? {};
      const prevRules = current.userRules ?? {};
      const rulesChanged =
        freshRules.attendanceImageConfig !== prevRules.attendanceImageConfig ||
        freshRules.faceRecognitionConfig !== prevRules.faceRecognitionConfig ||
        freshRules.workplaceConfig !== prevRules.workplaceConfig ||
        // Per-user shift-swap override (Enabled/Disabled/InheritedFromCompany); stale value
        // here makes the route guard mis-resolve a per-user opt-out (NAV-2).
        freshRules.shiftSwapConfig !== prevRules.shiftSwapConfig;
      if (rulesChanged) {
        dispatch(updateUserRules(payload.userRules));
      }
    } catch {
      // Non-fatal: keep showing the cached config if revalidation fails.
    }
  };
}

